import isNumber from 'lodash/isNumber';
import qs from 'qs';
import equal from 'fast-deep-equal';
import tracer from './tracer';

export const hasNoActiveFilter = (searchState = {}) =>
    !(
        searchState.sortBy ||
        searchState.range ||
        searchState.refinementList ||
        searchState.toggle ||
        searchState.multiRange ||
        searchState.query
    );

export const NAMESPACES = [
    'refinementList',
    'range',
    'toggle',
    'hierarchicalMenu',
    'menu',
    'multiRange',
    'sortBy',
];

export const getFilterIdByKey = (key = '', filtersConfig) => {
    let result;
    if (typeof key === 'string') {
        if (filtersConfig[key] && filtersConfig[key].id) {
            result = filtersConfig[key].id;
        } else {
            const updatedKey = encodeURIComponent(
                key.replace('catalog_attribute.', 'cat_attr_'),
            );
            if (updatedKey && updatedKey !== '') {
                result = updatedKey;
            }
        }
    }

    return result;
};

export const getFilterAttributeByKey = (key, filtersConfig) => {
    const mandatoryFilterKey = Object.keys(filtersConfig).find(
        filterKey =>
            filtersConfig[filterKey] && filtersConfig[filterKey].id === key,
    );
    if (mandatoryFilterKey) {
        return mandatoryFilterKey;
    }
    if (key === 'sort') {
        return 'sortBy';
    }
    if (key.match('cat_attr_')) {
        return key.replace('cat_attr_', 'catalog_attribute.');
    }
    return key;
};

export const createURL = (
    state,
    customState = {},
    indexes = [],
    filtersConfig,
    flags = {},
) => {
    const { hasInCategorySearchEnabled } = flags;
    const queryParams = {
        ...customState,
        redirect: undefined,
        ...Object.keys(state.toggle || {})
            .filter(key => state.toggle[key])
            .reduce(
                (object, key) => ({
                    ...object,
                    [getFilterIdByKey(key, filtersConfig)]: 1,
                }),
                {},
            ),
        ...Object.keys(state.refinementList || {}).reduce((object, key) => {
            const isArray = Array.isArray(state.refinementList[key]);
            if (
                isArray ||
                (typeof state.refinementList[key] === 'string' &&
                    state.refinementList[key].length > 0)
            ) {
                return {
                    ...object,
                    [getFilterIdByKey(key, filtersConfig)]: isArray
                        ? encodeURIComponent(
                              state.refinementList[key].join('__'),
                          )
                        : encodeURIComponent(state.refinementList[key]),
                };
            }
            return object;
        }, {}),
        ...Object.keys(state.range || {}).reduce(
            (object, key) => ({
                ...object,
                [getFilterIdByKey(key, filtersConfig)]: state.range[key],
            }),
            {},
        ),
        ...Object.keys(
            (state.manomano && state.manomano.urlParams) || {},
        ).reduce(
            (object, key) => ({
                ...object,
                [key]: state.manomano.urlParams[key],
            }),
            {},
        ),
    };

    if (state.configure?.page > 1) {
        queryParams.page = state.configure.page;
    }

    const indexValue = indexes.find(index => index.value === state.sortBy);

    if (indexValue && indexValue.urlValue) {
        queryParams.sort = indexValue.urlValue;
    }

    const trimMultiSpaceRegexp = / +|%20+/g;
    const url = {};
    if (state.query && state.query !== '') {
        const searchQuery = encodeURIComponent(state.query);
        const inCategorySearch = state.inCategorySearch;
        if (hasInCategorySearchEnabled && inCategorySearch) {
            url.pathname = inCategorySearch.url;
            queryParams.q = searchQuery;
        } else {
            delete queryParams.q;
            url.pathname = `/recherche/${(searchQuery || '')
                .trim()
                .replace(trimMultiSpaceRegexp, '+')}`;
        }
    }
    const { redirect, ...params } = queryParams;
    if (Object.keys(params).length || Object.keys(queryParams).length) {
        url.search = qs
            .stringify(queryParams, {
                addQueryPrefix: true,
                encode: false,
            })
            .replace(trimMultiSpaceRegexp, '+');
    }

    return url;
};

export const appStateToUrl = ({
    searchState = null,
    customState,
    indexes,
    filtersConfig,
    flags,
} = {}) => {
    if (searchState && Object.keys(searchState).length !== 0) {
        return createURL(
            searchState,
            customState,
            indexes,
            filtersConfig,
            flags,
        );
    }
    return '';
};

export const getSearchStateNamespaceFromFilterType = filterType => {
    /* eslint-disable indent */
    switch (filterType) {
        case 'color':
        case 'checkbox':
            return 'refinementList';
        case 'bool':
            return 'toggle';
        default:
            return filterType;
    }
    /* eslint-enable indent */
};

export const getSearchStateType = (key, value, filtersConfig) => {
    if (key === 'page' || key === 'sortBy' || key === 'model_id') {
        return null;
    }

    // if type is configured in config/filters
    if (filtersConfig[key] && filtersConfig[key].type) {
        return getSearchStateNamespaceFromFilterType(filtersConfig[key].type);
        // else we want to guess it
    }

    if (
        key === 'min' ||
        key === 'max' ||
        (value && Object.prototype.hasOwnProperty.call(value, 'min')) ||
        (value && Object.prototype.hasOwnProperty.call(value, 'max'))
    ) {
        return 'range';
    }

    if (value === 1 || value === 0) {
        return 'toggle';
    }

    if (typeof value === 'string' && key.startsWith('catalog_attribute')) {
        return 'refinementList';
    }

    return 'url_param';
};

export const getTransformedValueForInstantSearch = (value, type, indexes) => {
    if (type === 'sort') {
        return (indexes.find(index => index.urlValue === value) || indexes[0])
            .value;
    }
    if (type === 'toggle') {
        return value === '1' || value === 1;
    }
    if (type === 'range') {
        return {
            min:
                value && (value.min || value.min === 0)
                    ? parseFloat(value.min)
                    : undefined,
            max:
                value && (value.max || value.max === 0)
                    ? parseFloat(value.max)
                    : undefined,
        };
    }
    if (type === 'refinementList' && value.includes('__')) {
        return value.split('__');
    }
    if (typeof value === 'string' && isNumber(value)) {
        return parseFloat(value);
    }
    return value;
};

export const urlToAppState = tracer.wrap(
    'fragment.listing.urlToAppState',
    (location = {}, listingData, indexes, filtersConfig, flags = {}) => {
        const { hasInCategorySearchEnabled } = flags;
        const parsedSearch = qs.parse(location?.search, {
            plainObjects: true,
            ignoreQueryPrefix: true,
        });

        const searchState = Object.keys(parsedSearch)
            .filter(key => !['redirect'].includes(key))
            .reduce(
                (object, key) => {
                    const nextObject = object;
                    if (!nextObject.configure) {
                        nextObject.configure = {};
                    }

                    if (parsedSearch[key]) {
                        const attribute = getFilterAttributeByKey(
                            key,
                            filtersConfig,
                        );
                        const searchStateType = getSearchStateType(
                            attribute,
                            parsedSearch[key],
                            filtersConfig,
                        );
                        if (searchStateType) {
                            if (searchStateType === 'url_param') {
                                nextObject.manomano.urlParams = {
                                    ...object.manomano.urlParams,
                                    [attribute]: parsedSearch[key],
                                };
                            } else {
                                nextObject[searchStateType] = {
                                    ...object[searchStateType],
                                    [attribute]: getTransformedValueForInstantSearch(
                                        parsedSearch[key],
                                        searchStateType,
                                        indexes,
                                    ),
                                };
                            }
                        } else {
                            if (key === 'page') {
                                nextObject.configure.page = parseInt(
                                    parsedSearch[key],
                                    10,
                                );
                            }

                            nextObject[
                                attribute
                            ] = getTransformedValueForInstantSearch(
                                parsedSearch[key],
                                key,
                                indexes,
                            );
                        }
                    }
                    return nextObject;
                },
                { manomano: { urlParams: {} }, configure: {} },
            );
        if (location?.params?.query) {
            searchState.query = decodeURIComponent(location.params.query);
        }
        if (
            hasInCategorySearchEnabled &&
            parsedSearch.q &&
            parsedSearch.q !== ''
        ) {
            searchState.query = decodeURIComponent(parsedSearch.q);
        }

        switch (listingData.type) {
            case 'category':
                searchState.menu = {
                    [`categories.l${listingData.params?.level}.id`]: listingData
                        .params.id,
                };
                break;
            case 'brand':
                searchState.menu = {
                    brands: listingData.params?.name,
                };
                break;
            case 'search':
                searchState.query = listingData.query;
                break;
            default:
        }

        searchState.fromBrowserBack = !!location?.fromBrowserBack;

        return searchState;
    },
);

// @TODO found bette name
export const didUserUpdateSearchState = (
    prevSearchState,
    nextSearchState,
    { ignoreNavigation = false } = {},
) => {
    const prev = {
        page: prevSearchState.page,
        sortBy: prevSearchState.sortBy,
        ...prevSearchState.range,
        ...prevSearchState.refinementList,
        ...prevSearchState.toggle,
        ...prevSearchState.multiRange,
        ...prevSearchState.query,
    };

    const next = {
        page: nextSearchState.page,
        sortBy: nextSearchState.sortBy,
        ...nextSearchState.range,
        ...nextSearchState.refinementList,
        ...nextSearchState.toggle,
        ...nextSearchState.multiRange,
        ...nextSearchState.query,
    };

    if (ignoreNavigation) {
        delete prev.page;
        delete next.page;
    }

    return !equal(prev, next);
};

export const isRefined = (value, currentRefinement) => {
    if (Array.isArray(value)) {
        return value.some(element => element.value === currentRefinement);
    }
    return value === currentRefinement;
};

export const buildRulesContext = (
    searchState,
    listingData,
    modelId,
    {
        hasFFRerankingSearch, // 'ff.relevance.reranking.search'
        hasInCategorySearch, // 'relevance.in.category.search'
        hasRerankingSearchAlgorithm, // 'relevance.reranking.search.algorithm'
        hasShopbotRerankingAlternate, // 'relevance.shopbot.reranking.alternate'
    },
) => {
    const currentRulesContext = [];

    /**
     * Enable reranking on search pages
     * JIRA: REC-1819 + REC-1910
     */
    if (
        (!searchState.sortBy && listingData.type === 'search') ||
        (hasInCategorySearch &&
            !searchState.sortBy &&
            listingData.type === 'category' &&
            searchState?.manomano?.urlParams?.q)
    ) {
        if (hasFFRerankingSearch) {
            currentRulesContext.push(
                `reranking-search${
                    hasRerankingSearchAlgorithm ? '-alternate' : ''
                }`,
            );
        } else {
            currentRulesContext.push(
                `reranking-search-legacy${
                    hasRerankingSearchAlgorithm ? '-alternate' : ''
                }`,
            );
        }
    }

    if (listingData.type === 'category') {
        const { params } = listingData;
        if (params && params.id && modelId) {
            const rerankingVariation = hasShopbotRerankingAlternate
                ? '-alternate'
                : '';
            currentRulesContext.push(
                `shopbot_category_id-${params.id}_model_id-${modelId}${rerankingVariation}`,
            );
        }
    }
    return currentRulesContext;
};

export function getRulesContext(searchState, listingData, modelId, flags) {
    return searchState.menu && searchState.menu['categories.last']
        ? []
        : buildRulesContext(searchState, listingData, modelId, flags);
}

export default {
    appStateToUrl,
    urlToAppState,
    getSearchStateNamespaceFromFilterType,
    didUserUpdateSearchState,
    hasNoActiveFilter,
    NAMESPACES,
    isRefined,
    getRulesContext,
    buildRulesContext,
};
